import { Node } from "reactflow";
import { HTMLarkdown } from "htmlarkdown";
import { isHTML, removeOptionalBrTags, hasBrTags } from "../utils/DomHandler";
import { NodeData } from "../types/types";

export const getChildNodePosition = (
  event: React.MouseEvent<Element, MouseEvent>,
  parentNode?: Node,
  store?: any,
  project?: any
) => {
  const { domNode } = store.getState();

  if (
    !domNode ||
    !parentNode?.positionAbsolute ||
    !parentNode?.width ||
    !parentNode?.height
  ) {
    // console.log("parentNode not yet initialized", domNode);
    return;
  }

  const { top, left, width, height } = domNode.getBoundingClientRect();

  const paneCenterX = width / 2;
  const paneCenterY = height / 2;

  const panePosition = project({
    x: event.clientX - left - paneCenterX,
    y: event.clientY - top - paneCenterY,
  });

  return {
    x: panePosition.x - parentNode.positionAbsolute.x + parentNode.width / 2,
    y: panePosition.y - parentNode.positionAbsolute.y + parentNode.height / 2,
  };
};

export const selectedOrRootNode = (
  nodes: Node<NodeData>[],
  selectedNodeId: string | null
) => {
  let node;
  // console.log("selectedNodeId", selectedNodeId);
  node = nodes.find((node) => node.id === selectedNodeId);
  // console.info("selectedChildNode", node);
  if (!node) {
    node = nodes.find((node) => node.id === "root" || node.type === "center");
    // console.info("selectedRootNode", node);
  }

  return node;
};

/**
 * Finds a non-overlapping position for a new node using spiral search.
 * @param nodes Existing nodes (with position, width, height)
 * @param getViewport Function to get current viewport (must return {x, y, zoom})
 * @param flowMapWrapper Ref to the flow map wrapper (for offsetWidth/Height)
 * @param selectedNode Optional selected node (to make the new node a child)
 * @param ideaWidth Width of the new node (default 50)
 * @param ideaHeight Height of the new node (default 18)
 * @returns { x: number, y: number, parentId?: string }
 */
export function getNonOverlappingPosition({
  nodes,
  viewport,
  flowMapWrapper,
  selectedNode,
  ideaWidth = 50,
  ideaHeight = 18,
}: {
  nodes: Array<{
    position: { x: number; y: number };
    width?: number;
    height?: number;
  }>;
  viewport: any;
  flowMapWrapper: React.RefObject<HTMLDivElement>;
  selectedNode?: { id: string };
  ideaWidth?: number;
  ideaHeight?: number;
}): { x: number; y: number; parentId?: string } {
  // Get all node positions and sizes
  const nodeRects = nodes.map((n) => ({
    x: n.position.x,
    y: n.position.y,
    width: n.width ?? 50,
    height: n.height ?? 18,
  }));

  // Get viewport bounds
  const paneWidth = 800; // fallback if not available
  const paneHeight = 600;
  const minX = -viewport.x / viewport.zoom;
  const minY = -viewport.y / viewport.zoom;
  const maxX =
    minX + (flowMapWrapper.current?.offsetWidth || paneWidth) / viewport.zoom;
  const maxY =
    minY + (flowMapWrapper.current?.offsetHeight || paneHeight) / viewport.zoom;

  // Try to find a free spot using a spiral search
  let found = false;
  let angle = 0;
  let radius = 160; // Increased from 0 to 60 for extra spacing
  let tryCount = 0;
  let newFocusX = minX + (maxX - minX) / 2;
  let newFocusY = minY + (maxY - minY) / 2;
  let parentId: string | undefined = undefined;
  if (selectedNode) {
    parentId = selectedNode.id;
  }
  const maxTries = 100;

  while (!found && tryCount < maxTries) {
    // Spiral out from center
    newFocusX = minX + (maxX - minX) / 2 + radius * Math.cos(angle);
    newFocusY = minY + (maxY - minY) / 2 + radius * Math.sin(angle);

    // Check for overlap
    const overlaps = nodeRects.some(
      (rect) =>
        newFocusX < rect.x + rect.width &&
        newFocusX + ideaWidth > rect.x &&
        newFocusY < rect.y + rect.height &&
        newFocusY + ideaHeight > rect.y
    );

    if (
      !overlaps &&
      newFocusX > minX &&
      newFocusY > minY &&
      newFocusX + ideaWidth < maxX &&
      newFocusY + ideaHeight < maxY
    ) {
      found = true;
      break;
    }

    angle += Math.PI / 6;
    if (angle > 2 * Math.PI) {
      angle = 0;
      radius += 40;
    }
    tryCount++;
  }

  return { x: newFocusX, y: newFocusY, parentId };
}

export const convertHtmlToMarkdown = (inputHtml: string): string => {
  if (!isHTML(inputHtml)) {
    return inputHtml; // Return as is if not valid HTML
  }

  // Remove <br> tags if they are optional
  if (hasBrTags(inputHtml)) {
    inputHtml = removeOptionalBrTags(inputHtml);
  }
  const markdownConverter = new HTMLarkdown();
  const result = markdownConverter.convert(inputHtml);
  return result;
};

export const splitTextIntoTitleAndNotes = (
  inputText: string,
  inputHtml: string
): { title: string; notes: string } => {
  const markdownConverter = new HTMLarkdown();
  const titleLine = inputText.replace(/^\n+/, "").split("\n")[0].trim();
  const maxTitleLength = 50;

  // let markdown = markdownConverter.convert(inputHtml);
  // markdown = removeOptionalBrTags(markdown);

  let processedTitle = titleLine;
  if (processedTitle.length > maxTitleLength) {
    const lastSpace = processedTitle.lastIndexOf(" ", maxTitleLength);
    processedTitle =
      processedTitle.slice(0, lastSpace > 0 ? lastSpace : maxTitleLength) + "…";
  }

  //const notes = markdown.trim();

  return { title: processedTitle, notes: inputHtml };
};
